"use client";

import { motion } from "motion/react";
import { Star, TrendingUp, BadgeCheck } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

const successStories = [
  {
    name: "Maryam Ojora",
    location: "Allen Avenue, Ikeja",
    initials: "MO",
    rating: 5,
    savings: "₦1,450,000",
    story: "RentWise helped me avoid a property with hidden maintenance issues. The detailed reviews from previous tenants revealed everything the landlord wasn't telling me.",
    impact: "Saved in potential repair costs",
    tag: "Maintenance Issues"
  },
  {
    name: "Busola Raphael",
    location: "Bourdillon, Ikoyi",
    initials: "BR",
    rating: 5,
    savings: "₦980,000",
    story: "The rental price history feature showed me that the landlord was overcharging. I negotiated a fair price based on actual market data.",
    impact: "Saved in annual rent",
    tag: "Price Negotiation"
  },
  {
    name: "Moses Olumide",
    location: "Asokoro, Abuja",
    initials: "MO",
    rating: 5,
    savings: "₦750,000",
    story: "Thanks to community insights, I found a great neighborhood that wasn't on my radar. The area guides were spot-on about local amenities.",
    impact: "Saved in commuting costs",
    tag: "Location Research"
  }
];

const SuccessStories = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <section className="py-20 bg-linear-to-b from-rentwise-blue-50 to-white relative overflow-hidden">
      {/* Decorative elements */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 0.1, scale: 1 }}
        transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }}
        className="absolute top-40 -right-20 w-80 h-80 bg-rentwise-blue-300 rounded-full blur-3xl"
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 0.1, scale: 1 }}
        transition={{ duration: 2, repeat: Infinity, repeatType: "reverse", delay: 1 }}
        className="absolute -bottom-40 -left-20 w-80 h-80 bg-rentwise-blue-400 rounded-full blur-3xl"
      />

      <div className="container mx-auto px-4 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Success Stories
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Real stories from tenants who made smarter decisions with RentWise
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {successStories.map((story, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.2 }}
              className="bg-white rounded-2xl shadow-xl p-6 relative group hover:shadow-2xl transition-shadow duration-300"
            >
              {/* Success Tag */}
              <div className="absolute -top-3 right-6 bg-linear-to-r from-blue-600 to-indigo-600 text-white text-sm px-4 py-1 rounded-full">
                {story.tag}
              </div>

              {/* Profile Section */}
              <div className="flex items-center mb-6">
                <Avatar className="h-16 w-16">
                  <AvatarFallback className="bg-blue-100 text-blue-600 text-lg font-semibold">
                    {story.initials}
                  </AvatarFallback>
                </Avatar>
                <div className="ml-4">
                  <h3 className="font-semibold text-lg">{story.name}</h3>
                  <p className="text-gray-600 text-sm">{story.location}</p>
                  <div className="flex mt-1">
                    {[...Array(story.rating)].map((_, i) => (
                      <Star
                        key={i}
                        className="w-4 h-4 text-yellow-400 fill-yellow-400"
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Story Content */}
              <p className="text-gray-700 mb-6 leading-relaxed">
                "{story.story}"
              </p>

              {/* Impact Stats */}
              <div className="bg-blue-50 rounded-xl p-4 transform-gpu group-hover:scale-105 transition-transform duration-300">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <TrendingUp className="w-5 h-5 text-green-500 mr-2" />
                    <span className="text-gray-600 text-sm">{story.impact}</span>
                  </div>
                  <div className="flex items-center font-semibold text-blue-600">
                    <span>{story.savings}</span>
                  </div>
                </div>
              </div>

              {/* Verification Badge */}
              <div className="absolute top-6 right-6">
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  className="bg-green-100 p-2 rounded-full"
                >
                  <BadgeCheck className="w-5 h-5 text-green-600" />
                </motion.div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-16"
        >
                          <button className="bg-[var(--color-brand-primary)] hover:bg-[var(--color-brand-primary-hover)] text-white px-8 py-4 rounded-xl font-semibold transform-gpu hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl">
            Share Your Success Story
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default SuccessStories; 